import React, { useState, useEffect, useRef } from 'react';
import api from '../../services/api';
import { API_CHATBOT_BASE_URL } from '../../constants/config';
import PageHeader from '../../components/common/PageHeader';

const DocumentUpload = () => {
    const [documents, setDocuments] = useState([]);
    const [loading, setLoading] = useState(false);
    const [uploading, setUploading] = useState(false);
    const [file, setFile] = useState(null);
    const [title, setTitle] = useState('');
    const [description, setDescription] = useState('');
    const [success, setSuccess] = useState('');
    const [error, setError] = useState('');
    const pollingIntervalRef = useRef(null);

    useEffect(() => {
        fetchDocuments();
    }, []);

    // Separate effect for polling processing documents
    useEffect(() => {
        // Check if any documents are processing
        const hasProcessingDocs = documents.some(
            doc => doc.processing_status === 'pending' || doc.processing_status === 'processing'
        );

        if (hasProcessingDocs) {
            // Start polling if not already polling
            if (!pollingIntervalRef.current) {
                pollingIntervalRef.current = setInterval(() => {
                    fetchDocuments();
                }, 3000); // Poll every 3 seconds
            }
        } else {
            // Stop polling if no documents are processing
            if (pollingIntervalRef.current) {
                clearInterval(pollingIntervalRef.current);
                pollingIntervalRef.current = null;
            }
        }

        return () => {
            if (pollingIntervalRef.current) {
                clearInterval(pollingIntervalRef.current);
                pollingIntervalRef.current = null;
            }
        };
    }, [documents]); // Re-run when documents change

    const fetchDocuments = async () => {
        setLoading(true);
        try {
            // Fetch from reference API (RAG system)
            const response = await api.get(`${API_CHATBOT_BASE_URL}api/v1/admin/rag/documents`);
            const docs = response.data.data || response.data || [];
            // Ensure docs is an array
            setDocuments(Array.isArray(docs) ? docs : []);
        } catch (err) {
            console.error('Failed to fetch documents', err);
            // Don't show critical error on initial load if just empty or not set up
        } finally {
            setLoading(false);
        }
    };

    const handleFileChange = (e) => {
        setFile(e.target.files[0]);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!file) {
            setError('Please select a file');
            return;
        }

        setUploading(true);
        setError('');
        setSuccess('');

        try {
            // 1. Upload file using existing service (optional, if we want to store it in our bucket first)
            // Or directly to Reference API. Reference API likely needs the file.
            // Let's assume we send directly to Reference API as "rag should read from this docs".

            const formData = new FormData();
            formData.append('file', file);
            formData.append('title', title);
            formData.append('description', description);

            const response = await api.post(`${API_CHATBOT_BASE_URL}api/v1/admin/rag/documents`, formData, {
                headers: { 'Content-Type': 'multipart/form-data' }
            });

            // Show success message immediately - processing happens in background
            const docData = response.data.data || response.data;
            const processingStatus = docData?.processing_status || 'pending';

            if (processingStatus === 'pending' || processingStatus === 'processing') {
                setSuccess('Document uploaded successfully! Processing in background...');
            } else {
                setSuccess('Document uploaded and processed successfully!');
            }

            setTitle('');
            setDescription('');
            setFile(null);

            // Refresh list after a short delay to avoid blocking
            // The document will show with "pending" or "processing" status
            setTimeout(() => {
                fetchDocuments();
            }, 500);

        } catch (err) {
            console.error('Upload failed', err);
            const msg = err.response?.data?.message || err.response?.data?.detail || err.message || 'Unknown error';
            setError(`Failed to upload document. ${msg}`);
            // Check for auth error
            if (err.response?.status === 401) {
                setError('Authentication failed. Please try logging in again.');
            }
        } finally {
            setUploading(false);
        }
    };

    const handleDelete = async (id) => {
        if (!window.confirm('Are you sure you want to delete this document?')) return;
        try {
            await api.delete(`${API_CHATBOT_BASE_URL}api/v1/admin/rag/documents/${id}`);
            setDocuments(documents.filter(d => d.id !== id));
            setSuccess('Document deleted');
            setTimeout(() => setSuccess(''), 3000);
        } catch (err) {
            setError('Failed to delete document');
        }
    };

    return (
        <div className="min-h-screen bg-primary-50 pb-8">
            <PageHeader
                title="RAG Document Upload"
                subtitle="Upload documents (PDF) for the AI to learn about party information"
            />
            <div className="max-w-6xl mx-auto px-4">
                <div className="mb-6">
                    <button
                        onClick={() => window.history.back()}
                        className="flex items-center text-gray-600 hover:text-gray-900 transition-colors"
                    >
                        <svg className="w-4 h-4 mr-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                        </svg>
                        Back to Candidates
                    </button>
                </div>

                {error && <div className="mb-4 p-4 bg-red-50 text-red-700 rounded-lg">{error}</div>}
                {success && <div className="mb-4 p-4 bg-green-50 text-green-700 rounded-lg">{success}</div>}

                <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                    {/* Upload Form */}
                    <div className="lg:col-span-1">
                        <div className="bg-white rounded-lg shadow-md p-6">
                            <h2 className="text-xl font-semibold mb-4 text-gray-800">Upload New Document</h2>
                            <form onSubmit={handleSubmit} className="space-y-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Document Title</label>
                                    <input
                                        type="text"
                                        value={title}
                                        onChange={(e) => setTitle(e.target.value)}
                                        required
                                        className="w-full px-3 py-2 border rounded-md"
                                        placeholder="e.g. Party Constitution 2024"
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                                    <textarea
                                        value={description}
                                        onChange={(e) => setDescription(e.target.value)}
                                        rows={3}
                                        className="w-full px-3 py-2 border rounded-md"
                                        placeholder="What is this document about?"
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">File</label>
                                    <input
                                        type="file"
                                        onChange={handleFileChange}
                                        required
                                        accept=".pdf,.doc,.docx,.txt"
                                        className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-primary-50 file:text-primary-700 hover:file:bg-primary-100"
                                    />
                                    <p className="mt-1 text-xs text-gray-500">Supported: PDF</p>
                                </div>
                                <button
                                    type="submit"
                                    disabled={uploading}
                                    className="w-full bg-blue-600 text-white py-2 rounded-lg hover:bg-blue-700 disabled:opacity-50"
                                >
                                    {uploading ? 'Uploading...' : 'Upload & Process'}
                                </button>
                            </form>
                        </div>
                    </div>

                    {/* Document List */}
                    <div className="lg:col-span-2">
                        <div className="bg-white rounded-lg shadow-md overflow-hidden">
                            <div className="px-6 py-4 border-b border-gray-200">
                                <div className="flex justify-between items-center mb-3">
                                    <h2 className="text-xl font-semibold text-gray-800">Uploaded Documents</h2>
                                    {documents.some(doc => doc.processing_status === 'pending' || doc.processing_status === 'processing') && (
                                        <div className="flex items-center gap-2 text-sm text-blue-600">
                                            <svg className="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                            </svg>
                                            <span>Processing documents...</span>
                                        </div>
                                    )}
                                </div>
                                {documents.length > 0 && (
                                    <div className="flex gap-4 text-xs text-gray-600">
                                        <span className="flex items-center gap-1">
                                            <span className="w-2 h-2 bg-green-500 rounded-full"></span>
                                            Ready: {documents.filter(d => d.processing_status === 'completed' || !d.processing_status).length}
                                        </span>
                                        <span className="flex items-center gap-1">
                                            <span className="w-2 h-2 bg-blue-500 rounded-full animate-pulse"></span>
                                            Processing: {documents.filter(d => d.processing_status === 'processing' || d.processing_status === 'pending').length}
                                        </span>
                                        {documents.some(d => d.processing_status === 'failed') && (
                                            <span className="flex items-center gap-1">
                                                <span className="w-2 h-2 bg-red-500 rounded-full"></span>
                                                Failed: {documents.filter(d => d.processing_status === 'failed').length}
                                            </span>
                                        )}
                                    </div>
                                )}
                            </div>
                            {loading ? (
                                <div className="p-8 text-center text-gray-500">Loading documents...</div>
                            ) : documents.length === 0 ? (
                                <div className="p-8 text-center text-gray-500">No documents uploaded yet.</div>
                            ) : (
                                <ul className="divide-y divide-gray-200">
                                    {documents.map(doc => {
                                        const status = doc.processing_status || 'completed';
                                        const getStatusBadge = () => {
                                            switch (status) {
                                                case 'pending':
                                                    return (
                                                        <span className="px-2 py-1 text-xs font-medium bg-yellow-100 text-yellow-800 rounded-full flex items-center gap-1">
                                                            <span className="w-2 h-2 bg-yellow-600 rounded-full"></span>
                                                            Pending
                                                        </span>
                                                    );
                                                case 'processing':
                                                    return (
                                                        <span className="px-2 py-1 text-xs font-medium bg-blue-100 text-blue-800 rounded-full flex items-center gap-1">
                                                            <svg className="animate-spin h-3 w-3" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                                            </svg>
                                                            Processing
                                                        </span>
                                                    );
                                                case 'failed':
                                                    return (
                                                        <span className="px-2 py-1 text-xs font-medium bg-red-100 text-red-800 rounded-full flex items-center gap-1">
                                                            <span className="w-2 h-2 bg-red-600 rounded-full"></span>
                                                            Failed
                                                        </span>
                                                    );
                                                case 'completed':
                                                default:
                                                    return (
                                                        <span className="px-2 py-1 text-xs font-medium bg-green-100 text-green-800 rounded-full flex items-center gap-1">
                                                            <svg className="h-3 w-3" fill="currentColor" viewBox="0 0 20 20">
                                                                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                                                            </svg>
                                                            Ready
                                                        </span>
                                                    );
                                            }
                                        };

                                        return (
                                            <li key={doc.id} className="p-6 hover:bg-gray-50 flex justify-between items-start">
                                                <div className="flex-1">
                                                    <div className="flex items-center gap-2 mb-1">
                                                        <h3 className="text-lg font-medium text-gray-900">{doc.title}</h3>
                                                        {getStatusBadge()}
                                                    </div>
                                                    {doc.description && <p className="text-gray-600 mt-1">{doc.description}</p>}
                                                    <div className="flex items-center gap-4 mt-2 text-sm text-gray-500">
                                                        <span className="font-mono text-xs">{doc.filename}</span>
                                                        <span>•</span>
                                                        <span>{new Date(doc.uploaded_at || doc.created_at).toLocaleDateString()}</span>
                                                        {status === 'processing' && (
                                                            <>
                                                                <span>•</span>
                                                                <span className="text-blue-600">Processing in background...</span>
                                                            </>
                                                        )}
                                                    </div>
                                                    {status === 'failed' && doc.processing_error && (
                                                        <p className="text-xs text-red-600 mt-2">Error: {doc.processing_error}</p>
                                                    )}
                                                </div>
                                                <button
                                                    onClick={() => handleDelete(doc.id)}
                                                    className="text-red-500 hover:text-red-700 text-sm font-medium ml-4"
                                                >
                                                    Delete
                                                </button>
                                            </li>
                                        );
                                    })}
                                </ul>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default DocumentUpload;
